import React, { useEffect } from 'react';
import classNames from 'classnames';
import styles from './JiraTaskDetailDrawer.module.scss';

const formatDate = (value) => {
  if (!value) return null;
  const date = value?.toDate ? value.toDate() : new Date(value);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getStatusClass = (statusCategory) => {
  if (statusCategory === 'done') return styles.done;
  if (statusCategory === 'in-progress' || statusCategory === 'indeterminate')
    return styles.inProgress;
  return styles.todo;
};

const getTypeClass = (issueType) => {
  const t = issueType?.toLowerCase();
  if (t === 'bug') return styles.bug;
  if (t === 'story') return styles.story;
  if (t === 'task') return styles.task;
  if (t === 'epic') return styles.epic;
  return '';
};

const DetailItem = ({ label, value, muted = false }) => (
  <div className={styles.detailItem}>
    <span className={styles.detailLabel}>{label}</span>
    <span className={muted ? styles.detailValueMuted : styles.detailValue}>
      {value || <span className={styles.detailValueMuted}>—</span>}
    </span>
  </div>
);

const JiraTaskDetailDrawer = ({ task, onClose, siteUrl }) => {
  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const jiraIssueUrl =
    siteUrl && task?.key ? `${siteUrl}/browse/${task.key}` : null;

  return (
    <>
      <div className={styles.backdrop} onClick={onClose} />
      <div
        className={styles.drawer}
        role="dialog"
        aria-modal="true"
        aria-label={task?.summary}
      >
        {/* Header */}
        <div className={styles.drawerHeader}>
          <div className={styles.headerLeft}>
            {jiraIssueUrl ? (
              <a
                href={jiraIssueUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.issueKey}
              >
                {task?.key} ↗
              </a>
            ) : (
              <span className={styles.issueKey}>{task?.key}</span>
            )}
            <span className={styles.issueSummary}>{task?.summary}</span>
          </div>

          <div className={styles.headerActions}>
            {jiraIssueUrl && (
              <a
                href={jiraIssueUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.openInJiraBtn}
              >
                Open in Jira
              </a>
            )}
            <button
              className={styles.closeBtn}
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        {/* Body */}
        <div className={styles.drawerBody}>
          {/* Status / Type / Priority row */}
          <div className={styles.metaRow}>
            <span
              className={classNames(
                styles.statusTag,
                getStatusClass(task?.statusCategory)
              )}
            >
              {task?.status || 'Unknown'}
            </span>
            {task?.issueType && (
              <span
                className={classNames(
                  styles.typeTag,
                  getTypeClass(task.issueType)
                )}
              >
                {task.issueType}
              </span>
            )}
          </div>

          {/* Description */}
          {task?.description && (
            <div className={styles.section}>
              <span className={styles.sectionTitle}>Description</span>
              <p className={styles.description}>{task.description}</p>
            </div>
          )}

          <div className={styles.divider} />

          {/* Details grid */}
          <div className={styles.section}>
            <span className={styles.sectionTitle}>Details</span>
            <div className={styles.detailGrid}>
              <DetailItem
                label="Priority"
                value={task?.priority}
                muted={!task?.priority}
              />
              <DetailItem
                label="Assignee"
                value={task?.assigneeName}
                muted={!task?.assigneeName}
              />
              <DetailItem
                label="Reporter"
                value={task?.reporterName}
                muted={!task?.reporterName}
              />
              <DetailItem
                label="Project"
                value={task?.projectName}
                muted={!task?.projectName}
              />
              <DetailItem
                label="Due Date"
                value={task?.dueDate ? formatDate(task.dueDate) : null}
                muted={!task?.dueDate}
              />
              {task?.storyPoints != null && (
                <DetailItem
                  label="Story Points"
                  value={String(task.storyPoints)}
                />
              )}
              <DetailItem
                label="Created"
                value={formatDate(task?.jiraCreatedAt)}
                muted={!task?.jiraCreatedAt}
              />
              <DetailItem
                label="Updated"
                value={formatDate(task?.jiraUpdatedAt)}
                muted={!task?.jiraUpdatedAt}
              />
            </div>
          </div>

          {/* Labels */}
          {task?.labels?.length > 0 && (
            <>
              <div className={styles.divider} />
              <div className={styles.section}>
                <span className={styles.sectionTitle}>Labels</span>
                <div className={styles.labelsList}>
                  {task.labels.map((label) => (
                    <span key={label} className={styles.labelChip}>
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default JiraTaskDetailDrawer;
